import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Cuyana-style hybrid layout: when the stacked grid is shown beside the carousel
    // thumbnail rail, wire the thumbnails to scroll the grid (the slideshow itself is
    // unused on desktop, so we drive navigation directly rather than via its events).
    if (this.classList.contains('media-gallery--carousel-grid')) {
      this.#setupCarouselGrid(signal);
    }
  }

  #controller = new AbortController();

  /** Matches the desktop breakpoint where the grid (rather than the carousel) is shown. */
  #desktop = matchMedia('(min-width: 750px)');

  /** @type {IntersectionObserver | undefined} */
  #gridObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#gridObserver?.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Wires the thumbnail rail to the stacked grid for the hybrid layout.
   * Clicking a thumbnail scrolls the matching grid item to the top of the viewport,
   * and scrolling the grid keeps the matching thumbnail highlighted. Desktop only;
   * on mobile the native carousel is left untouched.
   * @param {AbortSignal} signal
   */
  #setupCarouselGrid(signal) {
    const thumbnails = this.#thumbnails();
    const items = this.#gridItems();
    if (!thumbnails.length || !items.length) return;

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener(
        'click',
        (event) => {
          if (!this.#desktop.matches) return;
          event.preventDefault();
          this.#scrollToGridItem(index);
          this.#setActiveThumbnail(index);
        },
        { signal }
      );
    });

    // Keep the active thumbnail in sync while the shopper scrolls the stacked grid.
    this.#gridObserver = new IntersectionObserver(
      (entries) => {
        if (!this.#desktop.matches) return;
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!mostVisible) return;
        const index = items.indexOf(/** @type {HTMLElement} */ (mostVisible.target));
        if (index >= 0) this.#setActiveThumbnail(index);
      },
      { threshold: [0.4, 0.6, 0.8] }
    );
    items.forEach((item) => this.#gridObserver?.observe(item));
  }

  /** @returns {HTMLButtonElement[]} The thumbnail buttons in the rail. */
  #thumbnails() {
    return /** @type {HTMLButtonElement[]} */ (
      Array.from(this.querySelectorAll('slideshow-controls[thumbnails] button'))
    );
  }

  /** @returns {HTMLElement[]} The stacked grid media items. */
  #gridItems() {
    const grid = this.querySelector('.media-gallery__grid');
    return grid ? /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(':scope > li'))) : [];
  }

  /**
   * Marks the thumbnail at the given index as selected.
   * @param {number} index
   */
  #setActiveThumbnail(index) {
    this.#thumbnails().forEach((thumbnail, i) => thumbnail.setAttribute('aria-selected', String(i === index)));
  }

  /**
   * Scrolls the window so the grid item sits near the top, clearing any sticky header.
   * The offset is read from the item's CSS scroll-margin-top so it stays tunable via CSS.
   * @param {number} index
   */
  #scrollToGridItem(index) {
    const item = this.#gridItems()[index];
    if (!item) return;

    const offset = parseInt(getComputedStyle(item).scrollMarginTop, 10) || 0;
    const top = Math.max(0, item.getBoundingClientRect().top + window.scrollY - offset);

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top, behavior: 'instant' });
      return;
    }

    this.#animateScrollTo(top);
  }

  /**
   * requestAnimationFrame-based smooth scroll. Used instead of `behavior: 'smooth'`
   * because CSS smooth scrolling can be suppressed in some embedded/preview contexts.
   * @param {number} top
   */
  #animateScrollTo(top) {
    const start = window.scrollY;
    const distance = top - start;
    if (distance === 0) return;

    const duration = Math.min(600, Math.max(200, Math.abs(distance) * 0.5));
    let startTime = null;

    const step = (now) => {
      if (startTime == null) startTime = now;
      const progress = Math.min(1, (now - startTime) / duration);
      // easeInOutQuad
      const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      window.scrollTo({ top: start + distance * eased, behavior: 'instant' });
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
